import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import LocalHotelIcon from "@mui/icons-material/LocalHotel";
import KingBedIcon from "@mui/icons-material/KingBed";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

export const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, "_");

export const BED_MANAGEMENT_PERMISSION = "manage beds";
export const BED_TYPE_MANAGEMENT_PERMISSION = "manage wards";
export const NURSE_MANAGEMENT_PERMISSION = "manage nurses";
export const DISCHARGE_TEMPLATE_PERMISSION = "manage discharge templates";

/**
 * Master tile catalog — single source of truth for permissions and display.
 *
 * Add a new tile:
 *   1. Append an entry to ALL_ACTIVITY_TILES
 *   2. Set sectionExclusive: true if it must not appear under OPD
 *   3. Add its path to ACTIVITY_SECTIONS.<section>.paths
 *
 * No changes needed in index.js.
 */
export const ALL_ACTIVITY_TILES = [
  {
    id: "manage patients",
    name: "Patient Management",
    path: "register_patient",
    icon: "/assets/images/newIcons/PM.svg",
  },
  {
    id: "view qr",
    name: "QR Registration",
    path: "scan_qr",
    icon: "/assets/images/newIcons/QR.svg",
  },
  {
    id: "manage appointments",
    name: "Appointment Management",
    path: "doctor/appointments",
    icon: "/assets/images/newIcons/AM.svg",
  },
  {
    id: "create and link visit purpose",
    name: "Create & Link Visit Purpose",
    path: "care_context",
    icon: "/assets/images/newIcons/Clvp.svg",
  },
  {
    id: "upload reports",
    name: "Upload Report",
    path: "upload_report",
    icon: "/assets/images/newIcons/UHR.svg",
  },
  {
    id: "manage billing and invoice",
    name: "Billing and Invoice",
    path: "doctor/item",
    icon: "/assets/images/newIcons/B.svg",
  },
  {
    id: "scan and share records",
    name: "Scan & Share Records",
    path: "scan_share_records",
    icon: "/assets/images/newIcons/SSR.svg",
  },
  {
    id: "manage facilities",
    name: "Facility Management",
    path: "facility_details",
    icon: "/assets/images/newIcons/FM.svg",
  },
  {
    id: "manage users",
    name: "User Management",
    path: "user_details",
    icon: "/assets/images/newIcons/UM.svg",
  },
  {
    id: "manage doctors",
    name: "Doctor Management",
    path: "facility_doctors",
    icon: "/assets/images/newIcons/DM.svg",
  },
  {
    id: NURSE_MANAGEMENT_PERMISSION,
    name: "Nurse Management",
    path: "nurse_management",
    icon: <MedicalServicesIcon />,
  },
  {
    id: DISCHARGE_TEMPLATE_PERMISSION,
    name: "Discharge Template Management",
    path: "discharge_template_management",
    icon: <FileDownloadIcon />,
  },
  {
    id: "manage billing and transaction",
    name: "Billing and Transaction",
    path: "transaction_records",
    icon: "/assets/images/newIcons/BAT.svg",
  },
  {
    id: "ipd_dashboard",
    name: "IPD Billing",
    path: "ipd-billing",
    icon: "/assets/images/newIcons/B.svg",
    sectionExclusive: true,
  },
  {
    // Corporate rate-card admin (manager). Reuses the "ipd_dashboard" entitlement
    // id so it is visible to the same manager/admin who can see IPD Billing,
    // without needing a new backend permission. TODO: switch to a dedicated id
    // (e.g. "corporate_rate_card") once the backend exposes that permission.
    id: "ipd_dashboard",
    name: "Corporate Rate Card",
    path: "corporate-rate-card",
    icon: "/assets/images/newIcons/BAT.svg",
    sectionExclusive: true,
    managerOnly: true, // Gated on the INVOICE_CANCEL_APPROVE billing permission
  },
  {
    // Scheme Service Names admin (manager). Sibling of Corporate Rate Card under
    // the Rate cards hub. Reuses the "ipd_dashboard" entitlement id, same rationale
    // as Corporate Rate Card above, until the backend exposes a dedicated permission.
    id: "ipd_dashboard",
    name: "Scheme Service Names",
    path: "scheme-service-names",
    icon: "/assets/images/newIcons/BAT.svg",
    sectionExclusive: true,
    managerOnly: true, // Gated on the INVOICE_CANCEL_APPROVE billing permission
  },
  {
    // Package billing admin (manager). Reuses "ipd_dashboard" entitlement id,
    // same rationale as Corporate Rate Card above, until the backend exposes a
    // dedicated permission. Stage A only — see readmd/ipd-package-billing-flow 1.md.
    id: "ipd_dashboard",
    name: "Package Billing",
    path: "package-billing",
    icon: "/assets/images/newIcons/B.svg",
    sectionExclusive: true,
    managerOnly: true, // Gated on the INVOICE_CANCEL_APPROVE billing permission
  },
  {
    // Service Master admin (manager). Reuses "ipd_dashboard" entitlement id,
    // same rationale as Corporate Rate Card / Package Billing above, until the
    // backend exposes a dedicated permission. Tenant-wide service catalog
    // consumed by Charge Capture + Corporate Rate Card.
    id: "ipd_dashboard",
    name: "Service Master",
    path: "service-master",
    icon: "/assets/images/newIcons/B.svg",
    sectionExclusive: true,
    managerOnly: true, // Gated on the INVOICE_CANCEL_APPROVE billing permission
  },
  {
    id: "manage_branding",
    name: "Branding Management",
    path: "manage_branding",
    icon: <BrandingWatermarkIcon />,
  },
  {
    id: "manage departments",
    name: "Department Management",
    path: "DepartmentManipulation",
    icon: "/assets/images/newIcons/DEPM.svg",
  },
  {
    id: BED_MANAGEMENT_PERMISSION,
    name: "Bed Management",
    path: "bed_management",
    icon: <LocalHotelIcon />,
  },
  {
    id: BED_TYPE_MANAGEMENT_PERMISSION,
    name: "Ward Management",
    path: "bed_type_management",
    icon: <KingBedIcon />,
  },
  {
    id: "manage lab service",
    name: "Lab Service Management",
    path: "laboratories",
    icon: "/assets/images/newIcons/LSM.svg",
    sectionExclusive: true,
  },
  {
    id: "manage radiology service",
    name: "Radiology Service Management",
    path: "radiology-services",
    icon: "/assets/images/newIcons/RSM.svg",
    sectionExclusive: true,
  },
  {
    id: "manage medicines",
    name: "Medicines Management",
    path: "pharmacy-user",
    icon: "/assets/images/newIcons/MM.svg",
  },
  {
    id: "manage doctors",
    name: "Schedule Management",
    path: "schedule-management",
    icon: "/Icons/AppointmentScheduleIcon.svg",
  },
  {
    id: "reports",
    name: "Reports & Analytics",
    path: "reports",
    icon: "/assets/images/newIcons/RAA.svg",
  },
  {
    id: "register_patient_ipd",
    name: "IPD Patient Admission",
    path: "register_patient_ipd",
    icon: "/assets/images/newIcons/PM.svg",
    sectionExclusive: true,
  },
  {
    id: "doctor_console_ipd",
    name: "Doctors Dashboard",
    path: "doctor_console_ipd",
    icon: "/assets/images/newIcons/DM.svg",
    sectionExclusive: true,
  },
  {
    // id stays "nurse_tasks" — entitlement matches tile id against the backend
    // permission_name; only the destination moved to the nurse shell.
    id: "nurse_tasks",
    name: "Nurses Dashboard",
    path: "nurse_dashboard",
    icon: "/assets/images/newIcons/AM.svg",
    sectionExclusive: true,
  },
  {
    id: "allocation_dashboard",
    name: "Bed Allocation",
    path: "allocation_dashboard",
    icon: "/assets/images/newIcons/PM.svg",
    sectionExclusive: true,
  },
  {
    // No "nursing_manager" permission exists in any tenant, so this tile is
    // entitled by the existing "nurse_dashboard" permission and then narrowed
    // to the nurse-manager role — a general nurse never sees it.
    id: "nurse_dashboard",
    name: "Nursing Manager",
    path: "nursing_manager",
    allowedRoles: ["nursing supervisor"],
    icon: "/assets/images/newIcons/AM.svg",
    sectionExclusive: true,
  },
  {
    id: "bed_transfers",
    name: "Bed Transfers",
    path: "bed_transfer_requisition",
    icon: "/assets/images/newIcons/AM.svg",
    sectionExclusive: true,
  },
  {
    id: "bed_retention_management",
    name: "Bed Retention",
    path: "bed_retention_management",
    icon: "/assets/images/newIcons/PM.svg",
    sectionExclusive: true,
  },
  {
    id: "bed_transfer_requisition",
    name: "Bed Transfer Requisition",
    path: "bed_transfer_requisition",
    icon: "/assets/images/newIcons/PM.svg",
    sectionExclusive: true,
  },
  {
    id: "admitted_discharged_patients",
    name: "Admitted / Discharged Patients",
    path: "admitted_discharged_patients",
    icon: "/assets/images/newIcons/PM.svg",
    sectionExclusive: true,
  },
  {
    id: "emergency_to_ipd",
    name: "Emergency to IPD",
    path: "emergency_to_ipd",
    icon: "/assets/images/newIcons/AM.svg",
    sectionExclusive: true,
  },
  {
    id: "ipd_approvals",
    name: "IPD Approvals",
    path: "ipd_approvals",
    icon: "/assets/images/newIcons/AM.svg",
    sectionExclusive: true,
  },
  {
    id: "cancellation_rules",
    name: "Cancellation Rules",
    path: "cancellation_rules",
    icon: "/assets/images/newIcons/DEPM.svg",
    sectionExclusive: true,
  },
  {
    id: "housekeeping_settings",
    name: "Housekeeping Settings",
    path: "housekeeping_settings",
    icon: "/assets/images/newIcons/DEPM.svg",
    sectionExclusive: true,
  },
  {
    id: "manage patients",
    name: "Patient Historical Records",
    path: "patient-local-data",
    icon: "/assets/images/newIcons/RAA.svg",
  },
  {
    id: "ot_dashboard",
    name: "OT Dashboard",
    path: "ot_dashboard",
    icon: "/assets/images/newIcons/DM.svg",
    sectionExclusive: true,
  },
  {
    id: "ot charge capture",
    name: "OT Charge Capture",
    path: "ot-charge-capture",
    icon: "/assets/images/newIcons/B.svg",
    sectionExclusive: true,
  },
  // The Inventory Suite splits into three workspaces, each with its own left sidebar
  // and overview, and each on its own entitlement. These three ids are also the suite's
  // module gates — see MODULE_GATES in pages/inventory/utils/inventoryRoles.js, which
  // normalises them to the same codes so the sidebar and this page agree on access.
  {
    id: "Inventory_dashboard",
    name: "Inventory Management",
    path: "inventory-suite/stock",
    icon: "/assets/images/newIcons/ProductInventory.svg",
    sectionExclusive: true,
  },
  {
    id: "Material_dashboard",
    name: "Material Management",
    path: "inventory-suite/material",
    icon: "/assets/images/newIcons/DM.svg",
    sectionExclusive: true,
  },
  {
    id: "Pharmacy_dashboard",
    name: "Pharmacy Management",
    path: "inventory-suite/pharmacy",
    icon: "/assets/images/newIcons/MM.svg",
    sectionExclusive: true,
  },
  {
    // One tile into the whole Emergency Department module. It lands on the
    // tracking board; the module's own left sidebar navigates from there to
    // registration, all-visits and the rest — so those no longer need their
    // own launcher tiles. Entitled by "emergency_dashboard" (module access);
    // the granular permissions still gate actions on their own routes inside.
    id: "emergency_dashboard",
    name: "Emergency Dashboard",
    path: "emergency_tracking_board",
    icon: "/assets/images/newIcons/AM.svg",
    sectionExclusive: true,
  },
  {
    id: "emergency_dashboard",
    name: "Department Setup",
    path: "emergency_department_setup",
    icon: "/assets/images/newIcons/DEPM.svg",
    sectionExclusive: true,
  },
];

/**
 * Section launcher config.
 *
 * Add a new section:
 *   1. Add a key with emptyLabel and ordered paths
 *   2. Add the matching entry in ActivityLauncherDrawer SIDE_NAV_ITEMS
 *
 * Add a card to an existing section:
 *   Append the tile path to that section's paths array.
 */
export const ACTIVITY_SECTIONS = {
  default: {
    emptyLabel: "No Entitlement",
  },
  ipd: {
    emptyLabel: "No IPD tiles",
    paths: [
      // "register_patient",
      "register_patient_ipd",
      "doctor_console_ipd",
      "nurse_dashboard",
      "nursing_manager",
      "allocation_dashboard",
      "bed_retention_management",
      "bed_transfer_requisition",
      "admitted_discharged_patients",
      "emergency_to_ipd",
      "ipd_approvals",
      "cancellation_rules",
      "housekeeping_settings",
      "ipd-billing",
      "corporate-rate-card",
      "scheme-service-names",
      "package-billing",
      "service-master",
      "inventory-suite/stock",
      "inventory-suite/material",
      "inventory-suite/pharmacy",
    ],
  },
  ot: {
    emptyLabel: "No OT tiles",
    paths: ["ot_dashboard", "ot-charge-capture"],
  },
  emergency: {
    emptyLabel: "No Emergency tiles",
    paths: [
      "emergency_tracking_board",
      "emergency_department_setup",
    ],
  },
  lab: {
    emptyLabel: "No Lab tiles",
    paths: ["laboratories"],
  },
  radiology: {
    emptyLabel: "No Radiology tiles",
    paths: ["radiology-services"],
  },
};

const tilesByPath = new Map(
  ALL_ACTIVITY_TILES.map((tile) => [tile.path, tile]),
);

const sectionKeys = Object.keys(ACTIVITY_SECTIONS);

export const VALID_SECTIONS = new Set(sectionKeys);

const buildSectionExclusiveSets = () => {
  const ids = new Set();
  const paths = new Set();

  ALL_ACTIVITY_TILES.forEach((tile) => {
    if (!tile.sectionExclusive) return;
    paths.add(tile.path);
    ids.add(tile.id);
  });

  return { ids, paths };
};

const { ids: sectionExclusiveIds, paths: sectionExclusivePaths } =
  buildSectionExclusiveSets();

export const SECTION_EXCLUSIVE_IDS = sectionExclusiveIds;
export const SECTION_EXCLUSIVE_PATHS = sectionExclusivePaths;

export const ROUTE_SECTION_MAP = sectionKeys.reduce((map, key) => {
  if (key === "default") return map;
  (ACTIVITY_SECTIONS[key].paths || []).forEach((path) => {
    map[path] = key;
  });
  return map;
}, {});

// Role gate for tiles that a permission alone can't separate (e.g. Nursing
// Manager, which rides on the shared "nurse_dashboard" permission). Role names
// are free-text and admin-defined per tenant — same vocabulary as the backend's
// Role.name — so `allowedRoles` lists them verbatim, matched case-insensitively.
// Tiles without `allowedRoles` are unrestricted.
export const tileAllowedForRole = (tile, roleName) => {
  if (!tile.allowedRoles) return true;
  const role = String(roleName || "").trim().toLowerCase();
  return tile.allowedRoles.some((allowed) => allowed.toLowerCase() === role);
};

// Match on `path` (the unique per-tile key), not `id`. Several tiles share an
// entitlement id (e.g. IPD Billing + Corporate Rate Card + Package Billing +
// Service Master all use "ipd_dashboard"). Matching by id would let a surviving
// same-id tile re-authorize sibling tiles that were intentionally filtered out
// (e.g. manager-only tiles removed for a user without INVOICE_CANCEL_APPROVE).
const tileIsAllowed = (tile, allowedItems) =>
  allowedItems.some((item) => item.path === tile.path);

export const buildSectionDisplayTiles = (paths, allowedItems) =>
  paths
    .map((path) => tilesByPath.get(path))
    .filter(Boolean)
    .filter((tile) => tileIsAllowed(tile, allowedItems));

export const getDisplayTilesForSection = (sectionKey, allowedItems) => {
  const section = ACTIVITY_SECTIONS[sectionKey];
  if (!section) return [];

  if (sectionKey === "default") {
    return allowedItems.filter(
      (item) =>
        !SECTION_EXCLUSIVE_IDS.has(item.id) &&
        !SECTION_EXCLUSIVE_PATHS.has(item.path),
    );
  }

  return buildSectionDisplayTiles(section.paths || [], allowedItems);
};

export const getSectionEmptyLabel = (sectionKey) =>
  ACTIVITY_SECTIONS[sectionKey]?.emptyLabel || "No Entitlement";

// Nav order matches ActivityLauncherDrawer SIDE_NAV_ITEMS.
export const ACTIVITY_SECTION_NAV_ORDER = [
  "default",
  "ipd",
  "emergency",
  "ot",
  "lab",
  "radiology",
  "Miscellaneous",
];

export const getAccessibleSections = (allowedItems) =>
  ACTIVITY_SECTION_NAV_ORDER.filter(
    (key) => getDisplayTilesForSection(key, allowedItems).length > 0,
  );

export const resolveActiveSection = (currentSection, allowedItems) => {
  const accessible = getAccessibleSections(allowedItems);
  if (accessible.length === 0) return "default";
  return accessible.includes(currentSection) ? currentSection : accessible[0];
};
